import { defineStore } from 'pinia';
import apiClient from '@/services/api'; // Adapter le chemin si nécessaire
import { ref, computed } from 'vue';
import type { User } from '@/types/user'; // Créer un fichier types/user.ts si nécessaire
import { useRouter } from 'vue-router';

// Définir l'interface User (peut être dans un fichier séparé types/user.ts)
// export interface User {
//   _id: string;
//   nom: string;
//   prenoms: string;
//   phone: string;
//   mail: string;
//   role: 'client' | 'admin' | 'superAdmin';
//   post?: string;
//   emailVerified: boolean;
//   isActive: boolean;
//   createdAt?: string;
//   updatedAt?: string;
// }

export const useAuthStore = defineStore('auth', () => {
  const router = useRouter();
  // State
  const user = ref<User | null>(null);
  const isAuthenticated = ref<boolean>(false);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Getters (computed properties)
  const currentUser = computed(() => user.value);
  const isLoggedIn = computed(() => isAuthenticated.value);
  const userRole = computed(() => user.value?.role);
  const isSuperAdmin = computed(() => user.value?.role === 'superAdmin');
  const isAdmin = computed(() => user.value?.role === 'admin');
  const isClient = computed(() => user.value?.role === 'client');
  const isServiceClient = computed(() => isAdmin.value && user.value?.post === 'service_client');
  const canViewOwnerInfo = computed(() => isSuperAdmin.value || isServiceClient.value);
  const canManageApartments = computed(() => isSuperAdmin.value || isAdmin.value);

  // Actions
  async function fetchUser() {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.get<{ success: boolean; user: User }>('/auth/me');
      if (response.data.success) {
        user.value = response.data.user;
        isAuthenticated.value = true;
      } else {
        await clearAuthData();
      }
    } catch (err: any) {
      //console.error('Failed to fetch user:', err);
      await clearAuthData();
      // Ne pas définir d'erreur ici car cela peut arriver si le cookie est invalide/expiré
    } finally {
      isLoading.value = false;
    }
  }

  async function login(credentials: { mail: string; password: string }) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.post<{ success: boolean; user: User; token?: string }>('/auth/login', credentials);
      if (response.data.success) {
        user.value = response.data.user;
        isAuthenticated.value = true;
        return true; // Indiquer le succès
      } else {
        // L'API devrait renvoyer un message d'erreur spécifique
        error.value = (response.data as any).message || 'Échec de la connexion.';
        return false;
      }
    } catch (err: any) {
      //console.error('Login error:', err);
      error.value = err.response?.data?.message || 'Une erreur est survenue lors de la connexion.';
      await clearAuthData();
      return false;
    } finally {
      isLoading.value = false;
    }
  }

  async function register(userData: any) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/auth/register', userData);
      if (response.data.success) {
        // L'utilisateur n'est pas connecté après l'inscription, il doit vérifier son email
        return { success: true, message: response.data.message };
      } else {
        error.value = response.data.message || 'Échec de l\'inscription.';
        return { success: false, message: error.value };
      }
    } catch (err: any) {
      //console.error('Registration error:', err);
      error.value = err.response?.data?.message || 'Une erreur est survenue lors de l\'inscription.';
      return { success: false, message: error.value };
    } finally {
      isLoading.value = false;
    }
  }

  async function logout() {
    isLoading.value = true;
    error.value = null;
    try {
      await apiClient.get('/auth/logout');
    } catch (err: any) {
      //console.error('Logout error:', err);
      // Même si l'appel API échoue, on nettoie côté client
    } finally {
      await clearAuthData();
      isLoading.value = false;
      router.push('/'); // 👈 Redirection vers la page d’accueil
    }
  }

  async function clearAuthData() {
    user.value = null;
    isAuthenticated.value = false;
    // Pas besoin de supprimer le cookie httpOnly manuellement, le backend s'en charge
    // ou il expirera / sera écrasé lors de la prochaine connexion.
  }

  async function forgotPassword(email: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.post<{ success: boolean; message: string }>('/auth/forgotpassword', { mail: email });
      return response.data; // Contient { success: true, message: "..." }
    } catch (err: any) {
      //console.error('Forgot password error:', err);
      error.value = err.response?.data?.message || 'Une erreur est survenue.';
      // Retourner une réponse générique même en cas d'erreur pour la sécurité
      return { success: true, message: "Si un compte avec cet email existe et est actif, un lien de réinitialisation a été envoyé." };
    } finally {
      isLoading.value = false;
    }
  }

  async function resetPassword(token: string, password: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.put<{ success: boolean; message: string }>(`/auth/resetpassword/${token}`, { password });
      return response.data; // { success: true, message: "..." } ou { success: false, message: "..." }
    } catch (err: any) {
      //console.error('Reset password error:', err);
      error.value = err.response?.data?.message || 'Une erreur est survenue lors de la réinitialisation.';
      return { success: false, message: error.value };
    } finally {
      isLoading.value = false;
    }
  }

  async function verifyEmail(token: string) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await apiClient.get<{ success: boolean; message: string }>(`/auth/verifyemail/${token}`);
      return response.data;
    } catch (err: any) {
      //console.error('Verify email error:', err);
      error.value = err.response?.data?.message || 'Une erreur est survenue lors de la vérification.';
      return { success: false, message: error.value };
    } finally {
      isLoading.value = false;
    }
  }

  async function updateUserDetails(details: Partial<User>) {
     isLoading.value = true;
     error.value = null;
     try {
       const response = await apiClient.put<{ success: boolean; user: User }>('/auth/updatedetails', details);
       if (response.data.success) {
         user.value = response.data.user; // Mettre à jour l'utilisateur dans le store
         return { success: true, user: response.data.user };
       } else {
         error.value = (response.data as any).message || 'Échec de la mise à jour.';
         return { success: false, message: error.value };
       }
     } catch (err: any) {
       //console.error('Update details error:', err);
       error.value = err.response?.data?.message || 'Une erreur est survenue lors de la mise à jour.';
       return { success: false, message: error.value };
     } finally {
       isLoading.value = false;
     }
  }

  // Initialiser le store en essayant de récupérer l'utilisateur si un cookie existe
  // Cela est utile si l'utilisateur recharge la page
  // fetchUser(); // Décommentez si vous voulez charger l'utilisateur au démarrage de l'app
  // Ou appelez fetchUser() dans votre fichier main.ts ou App.vue après la création de Pinia

  return {
    user,
    isAuthenticated,
    isLoading,
    error,
    currentUser,
    isLoggedIn,
    userRole,
    isSuperAdmin,
    isAdmin,
    isClient,
    isServiceClient,
    canViewOwnerInfo,
    canManageApartments,
    fetchUser,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    verifyEmail,
    updateUserDetails,
    clearAuthData
  };
});
